if (!customElements.get('cart-free-product')) {
  customElements.define(
    'cart-free-product',
    class CartFreeProduct extends HTMLElement {
      constructor() {
        super();
        this._handleClick = this._handleClick.bind(this);
        this._handleVariantChange = this._handleVariantChange.bind(this);
        this._handleCartUpdate = this._handleCartUpdate.bind(this);
        this._initialized = false;
        this._checkInterval = null;
        this._isUpdatingCart = false;
      }

      connectedCallback() {
        requestAnimationFrame(() => this._init());
        
        // Écouter les mises à jour du cart drawer
        document.addEventListener('deployer-cart-drawer:updated', this._handleCartUpdate);
        
        // Gérer le retour via bfcache
        window.addEventListener('pageshow', (event) => {
          if (event.persisted) {
            requestAnimationFrame(() => this._init());
          }
        });
      }

      disconnectedCallback() {
        this._cleanup();
        if (this._checkInterval) {
          clearInterval(this._checkInterval);
          this._checkInterval = null;
        }
        document.removeEventListener('deployer-cart-drawer:updated', this._handleCartUpdate);
      }

      _handleCartUpdate() {
        // Ne pas réinitialiser pendant une mise à jour en cours
        if (this._isUpdatingCart) return;
        
        // Ne pas réinitialiser si le panel est ouvert
        if (this._panel?.classList.contains('active')) return;
        
        // Délai pour laisser le DOM se mettre à jour
        setTimeout(() => {
          this._init();
        }, 200);
      }

      _init() {
        // Nettoyer les anciens listeners
        this._cleanup();
        
        // Récupérer les éléments
        this._freeProductContainer = this.querySelector('.cart__free-product-container');
        this._panel = this.querySelector('.product-panel');
        
        if (!this._freeProductContainer || !this._panel) return;
        
        this._freeProductTile = this._freeProductContainer.querySelector('.product-tile');
        if (!this._freeProductTile) return;

        // Récupérer les IDs des variantes des produits gratuits configurés
        this._freeVariantIds = this._getFreeVariantIds();
        
        // Si un cadeau est déjà dans le panier (via Liquid), nettoyer le flag "pas de cadeau"
        const selectedId = this._freeProductTile.dataset.selectedId;
        if (selectedId && selectedId !== '') {
          // Vérifier si c'est un vrai cadeau (pas le premier produit par défaut)
          // Le Liquid met l'ID du cadeau si un cadeau est dans le panier
          this._checkGiftInCartAndClearFlag();
        }

        // Utiliser la délégation d'événements sur le composant racine
        this.addEventListener('click', this._handleClick);
        this.addEventListener('product-tile:variant-change', this._handleVariantChange);
        
        // Initialiser l'affichage
        this._initDisplay();
        this._initialized = true;
      }
      
      async _checkGiftInCartAndClearFlag() {
        try {
          const cartResponse = await fetch(window.Shopify.routes.root + 'cart.js');
          const cart = await cartResponse.json();
          
          // Vérifier si un cadeau est dans le panier
          const hasGift = cart.items.some(item => {
            const isGiftProperty = item.properties && item.properties._is_gift === true;
            const isFreeVariant = this._freeVariantIds.includes(item.variant_id);
            return isGiftProperty || isFreeVariant;
          });
          
          if (hasGift) {
            localStorage.removeItem('cart_no_gift_selected');
          }
        } catch (error) {
          // Ignorer les erreurs silencieusement
        }
      }

      _cleanup() {
        this.removeEventListener('click', this._handleClick);
        this.removeEventListener('product-tile:variant-change', this._handleVariantChange);
        this._initialized = false;
      }

      _getFreeVariantIds() {
        // Récupérer les IDs depuis le validateur ou les produits du panel
        const validator = document.querySelector('free-product-validator[data-free-variant-ids]');
        if (validator) {
          try {
            return JSON.parse(validator.dataset.freeVariantIds);
          } catch {
            return [];
          }
        }
        
        // Fallback: extraire des produits du panel
        const ids = [];
        const products = this._panel?.querySelectorAll('[data-product-tile][data-selected-id]');
        products?.forEach(p => {
          const id = parseInt(p.dataset.selectedId, 10);
          if (id) ids.push(id);
        });
        return ids;
      }

      _handleClick(e) {
        const target = e.target;
        
        // Bouton "Changer" - chercher dans l'encart principal uniquement
        const changeButton = target.closest('.cart__free-product-container .product-tile__quick-select-button');
        if (changeButton) {
          e.preventDefault();
          e.stopPropagation();
          this._openPanel();
          return;
        }
        
        // Bouton de fermeture
        if (target.closest('.product-panel__close')) {
          e.preventDefault();
          this._closePanel();
          return;
        }
        
        // Clic sur l'overlay du panel
        if (target.classList.contains('product-panel') || target.classList.contains('drawer-panel')) {
          this._closePanel();
          return;
        }
        
        // Boutons "Sélectionner" dans le panel
        const selectButton = target.closest('.product-panel .product-tile__quick-select-button');
        if (selectButton) {
          e.preventDefault();
          const productTile = selectButton.closest('[data-product-tile]');
          if (productTile) {
            this._selectProduct(productTile, selectButton);
          }
          return;
        }
      }

      _handleVariantChange(e) {
        if (e.detail?.productTile) {
          this._updateFreeProduct(e.detail.productTile);
        }
      }

      _initDisplay() {
        const freeProductSubtitle = this._freeProductTile?.querySelector('[data-subtitle-color]');
        const products = this._panel.querySelectorAll('[data-product-tile]');
        
        // Si l'utilisateur a choisi "pas de cadeau", afficher l'option vide dans l'encart
        if (localStorage.getItem('cart_no_gift_selected') === 'true') {
          const emptyOption = this._panel?.querySelector('.featured-products__item--empty [data-product-tile]');
          if (emptyOption) {
            this._updateFreeProduct(emptyOption);
            // Marquer l'option vide comme sélectionnée dans le panel
            const allProducts = this._panel.querySelectorAll('[data-product-tile]');
            allProducts.forEach(p => p.classList.remove('selected'));
            emptyOption.classList.add('selected');
          }
          return;
        }
        
        // Si le sous-titre a déjà du contenu (pré-rempli par Liquid), ne pas l'écraser
        if (freeProductSubtitle && freeProductSubtitle.textContent.trim() !== '') {
          return;
        }
        
        if (products.length > 0 && freeProductSubtitle) {
          const firstProduct = products[0];
          const optionName = firstProduct.querySelector('.product-tile__option-name');
          const optionValue = firstProduct.querySelector('.product-tile__dropdown-text');

          if (optionName) {
            freeProductSubtitle.innerHTML = optionName.innerHTML;
            const optionValueEl = freeProductSubtitle.querySelector('[data-option-value]');
            if (optionValueEl && optionValue) {
              optionValueEl.innerHTML = optionValue.innerHTML;
            }
          }
        }
      }

      _openPanel() {
        if (!this._panel) return;
        // Force reflow pour s'assurer que l'animation fonctionne
        this._panel.offsetHeight;
        this._panel.classList.add('animate', 'active');
      }

      _closePanel() {
        if (!this._panel) return;
        this._panel.classList.remove('active');
      }

      async _selectProduct(productTile, button) {
        // Mettre à jour tous les boutons du panel
        const allProducts = this._panel.querySelectorAll('[data-product-tile]');
        allProducts.forEach(p => {
          p.classList.remove('selected');
          const btn = p.querySelector('.product-tile__quick-select-button');
          const label = btn?.querySelector('.product-tile__quick-select-selected-label');
          if (label && btn?.dataset?.label) {
            label.textContent = btn.dataset.label;
          }
        });
        
        // Marquer comme sélectionné
        productTile.classList.add('selected');
        const selectedLabel = button.querySelector('.product-tile__quick-select-selected-label');
        if (selectedLabel && button.dataset?.selectedLabel) {
          selectedLabel.textContent = button.dataset.selectedLabel;
        }

        // Vérifier si c'est l'option "pas de cadeau"
        const isEmptyOption = productTile.closest('.featured-products__item--empty') !== null 
                           || productTile.classList.contains('product-tile--empty');

        const newVariantId = isEmptyOption ? null : parseInt(productTile.dataset?.selectedId, 10);

        // Mémoriser le choix "pas de cadeau" dans localStorage
        if (isEmptyOption) {
          localStorage.setItem('cart_no_gift_selected', 'true');
        } else {
          localStorage.removeItem('cart_no_gift_selected');
        }

        // Mettre à jour le panier avec le nouveau cadeau
        const success = await this._updateCartWithGift(newVariantId);

        // Mettre à jour l'encart principal
        this._updateFreeProduct(productTile);
        
        // Fermer le panel
        this._closePanel();
        
        // Rafraîchir le panier après la fermeture du panel
        if (success) {
          this._isUpdatingCart = false;
          // Petit délai pour laisser l'animation de fermeture se terminer
          setTimeout(() => {
            document.dispatchEvent(new CustomEvent('deployer-cart-drawer:refresh'));
          }, 300);
        } else {
          this._isUpdatingCart = false;
        }
      }

      async _updateCartWithGift(newVariantId) {
        if (this._isUpdatingCart) return false;
        this._isUpdatingCart = true;

        try {
          // Récupérer le panier actuel
          const cartResponse = await fetch(window.Shopify.routes.root + 'cart.js');
          const cart = await cartResponse.json();

          // Trouver les produits gratuits actuels dans le panier
          const currentFreeItems = cart.items.filter(item => {
            // Vérifier si c'est un produit gratuit via _is_gift ou via les variant IDs configurés
            const isGiftProperty = item.properties && item.properties._is_gift === true;
            const isFreeVariant = this._freeVariantIds.includes(item.variant_id);
            return isGiftProperty || isFreeVariant;
          });

          // Retirer les produits gratuits actuels
          if (currentFreeItems.length > 0) {
            const updates = {};
            for (const item of currentFreeItems) {
              updates[item.key] = 0;
            }
            
            await fetch(window.Shopify.routes.root + 'cart/update.js', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json'
              },
              body: JSON.stringify({ updates })
            });
          }

          // Ajouter le nouveau cadeau si un produit est sélectionné
          if (newVariantId) {
            await fetch(window.Shopify.routes.root + 'cart/add.js', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json'
              },
              body: JSON.stringify({
                items: [{
                  id: newVariantId,
                  quantity: 1,
                  properties: {
                    _is_gift: true
                  }
                }]
              })
            });
          }

          return true;
        } catch (error) {
          console.error('Erreur lors de la mise à jour du cadeau:', error);
          return false;
        }
      }

      _updateFreeProduct(productTile) {
        if (!this._freeProductTile) return;

        const freeImageWrapper = this._freeProductTile.querySelector('.product-tile__image-wrapper');
        const freeTitle = this._freeProductTile.querySelector('.product-tile__title');
        const freeSubtitle = this._freeProductTile.querySelector('[data-subtitle-color]');

        const selectedImageWrapper = productTile.querySelector('.product-tile__image-wrapper');
        const selectedTitle = productTile.querySelector('.product-tile__title');
        const selectedSubtitle = productTile.querySelector('[data-subtitle-color]');

        // Vérifier si c'est l'option "pas de cadeau" (élément avec la classe --empty)
        const isEmptyOption = productTile.closest('.featured-products__item--empty') !== null 
                           || productTile.classList.contains('product-tile--empty');

        // Mettre à jour l'ID de la variante sélectionnée
        // Si c'est l'option "vide", on met une chaîne vide pour ne pas ajouter de cadeau
        if (isEmptyOption) {
          this._freeProductTile.setAttribute('data-selected-id', '');
        } else if (productTile.dataset?.selectedId) {
          this._freeProductTile.setAttribute('data-selected-id', productTile.dataset.selectedId);
        }

        if (freeImageWrapper && selectedImageWrapper) {
          freeImageWrapper.innerHTML = selectedImageWrapper.innerHTML;
        }
        if (freeTitle && selectedTitle) {
          freeTitle.innerHTML = selectedTitle.innerHTML;
        }
        if (freeSubtitle && selectedSubtitle) {
          freeSubtitle.innerHTML = selectedSubtitle.innerHTML;
        }
      }
    }
  );
}
